import axios from "./axiosInstance";
import config from "./config";

/**
 * Roster flags — the medical / no-photos / waiver-missing marks every roster
 * shows. One backend source (services/rosterFlags), one endpoint, one
 * component (components/attendance/RosterFlags).
 */

export interface MedicalDetailLine {
  label: string;
  text: string;
}

/** What is worth a coach's attention about one person. Absent keys are false. */
export interface RosterFlags {
  medical?: boolean;
  /** Present ONLY when this caller may already read medical information. */
  medical_detail?: MedicalDetailLine[];
  no_photos?: boolean;
  waiver_missing?: boolean;
  missing_waivers?: string[];
}

export interface RosterFlagsResult {
  /** Keyed by athlete id. Only athletes with something to flag appear. */
  athletes: Record<string, RosterFlags>;
  /** Keyed by registration id (event / camp / clinic rosters). */
  registrations: Record<string, RosterFlags>;
  checks: {
    medical: boolean;
    /** false = the club's database cannot answer the photo question yet. */
    photos: boolean;
    /** false = the club requires no waivers, so nobody is "missing" one. */
    waivers: boolean;
  };
  medical_detail_included: boolean;
}

/**
 * Reads the flags for a roster. The staff pages use `fetchRosterFlags`; the
 * kiosk passes its own (same answer, asked with the kiosk's sign-in).
 * Must throw on any failure.
 */
export type RosterFlagsFetcher = (subjects: {
  athleteIds?: string[];
  registrationIds?: string[];
}) => Promise<RosterFlagsResult>;

/** The one reading of the endpoint's answer. Throws when it is not one. */
export function parseRosterFlagsResponse(body: any): RosterFlagsResult {
  const data = body?.data;
  if (!body?.success || !data || typeof data !== "object") {
    throw new Error("Malformed roster flags response");
  }
  return {
    athletes: data.athletes || {},
    registrations: data.registrations || {},
    checks: {
      medical: data.checks?.medical !== false,
      photos: data.checks?.photos !== false,
      waivers: data.checks?.waivers === true,
    },
    medical_detail_included: data.medical_detail_included === true,
  };
}

/** Throws on any failure: an unchecked roster must never read as a clean one. */
export async function fetchRosterFlags(
  token: string | null,
  subjects: { athleteIds?: string[]; registrationIds?: string[] }
): Promise<RosterFlagsResult> {
  const res = await axios.post(
    `${config.BASE_URL}/attendance/roster-flags`,
    {
      athlete_ids: subjects.athleteIds || [],
      registration_ids: subjects.registrationIds || [],
    },
    {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    }
  );
  return parseRosterFlagsResponse(res?.data);
}
